import React from 'react';
import LegalPageLayout from '../../components/Legal/LegalPageLayout';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'data-collected', label: 'Data We Collect' },
  { id: 'how-we-use', label: 'How We Use Data' },
  { id: 'data-sharing', label: 'Data Sharing' },
  { id: 'cookies', label: 'Cookies & Tracking' },
  { id: 'data-retention', label: 'Data Retention' },
  { id: 'your-rights', label: 'Your Rights' },
  { id: 'security', label: 'Data Security' },
  { id: 'children', label: "Children's Privacy" },
  { id: 'changes', label: 'Policy Changes' },
  { id: 'contact', label: 'Contact Us' },
];

const PrivacyPolicy = () => (
  <LegalPageLayout
    title="Privacy Policy"
    subtitle="How CRM Automax collects, uses, and protects your personal information."
    lastUpdated="May 7, 2025"
    sections={sections}
  >
    <div className="legal-section" id="overview" data-section>
      <h2>1. Overview</h2>
      <p>
        CRM Automax ("we," "our," or "us") is committed to protecting the privacy of our users.
        This Privacy Policy explains how we collect, use, disclose, and safeguard your information
        when you use our cloud-based Customer Relationship Management (CRM) platform and associated
        services (collectively, the "Service").
      </p>
      <p>
        This policy is compliant with India's <strong>Digital Personal Data Protection Act (DPDPA) 2023</strong> and
        applicable international standards including the GDPR where relevant. By using our Service,
        you agree to the collection and use of information in accordance with this policy.
      </p>
      <div className="legal-highlight">
        <p>
          <strong>Data Fiduciary:</strong> CRM Automax acts as the Data Fiduciary for personal data
          collected through our website and platform. For data uploaded by you into the CRM (your customer/lead
          data), you are the Data Fiduciary and we act as a Data Processor.
        </p>
      </div>
    </div>

    <div className="legal-section" id="data-collected" data-section>
      <h2>2. Data We Collect</h2>
      <h3>2.1 Account & Registration Data</h3>
      <ul>
        <li>Full name and company name</li>
        <li>Work email address</li>
        <li>Phone number</li>
        <li>Password (stored in hashed/encrypted form — never in plain text)</li>
        <li>User role and permission level</li>
      </ul>

      <h3>2.2 Payment & Billing Data</h3>
      <p>
        Payment transactions are processed through <strong>Razorpay</strong>, a PCI-DSS Level 1
        compliant and RBI-authorised Payment Aggregator. We do <strong>not</strong> store your
        complete card number, CVV, or UPI PIN on our servers. We retain:
      </p>
      <ul>
        <li>Subscription plan details and billing history</li>
        <li>Razorpay payment ID and order ID (for transaction reference)</li>
        <li>GST information (if applicable)</li>
        <li>Invoice records required under Indian tax law</li>
      </ul>

      <h3>2.3 Usage & Activity Data</h3>
      <ul>
        <li>Pages visited and features used within the platform</li>
        <li>Login timestamps and session duration</li>
        <li>Actions taken within the CRM (lead creation, status updates, etc.)</li>
        <li>IP address and browser/device type</li>
        <li>Error logs and performance data</li>
      </ul>

      <h3>2.4 CRM Data (Your Customer Data)</h3>
      <p>
        Data you upload into the CRM — such as lead names, phone numbers, addresses, and interaction
        history — belongs to you. We process this data solely to provide the Service and do not use
        it for our own marketing or analytics.
      </p>

      <h3>2.5 Communication Data</h3>
      <ul>
        <li>Support tickets and email correspondence with our team</li>
        <li>Feedback and survey responses</li>
      </ul>
    </div>

    <div className="legal-section" id="how-we-use" data-section>
      <h2>3. How We Use Your Data</h2>
      <p>We use the information we collect for the following purposes:</p>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Purpose</th>
            <th>Legal Basis (DPDPA)</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Providing and maintaining the Service</td><td>Contractual Necessity</td></tr>
          <tr><td>Processing subscription payments via Razorpay</td><td>Contractual Necessity</td></tr>
          <tr><td>Sending transactional emails (invoices, alerts)</td><td>Contractual Necessity</td></tr>
          <tr><td>Account security and fraud prevention</td><td>Legitimate Interest</td></tr>
          <tr><td>Customer support and issue resolution</td><td>Contractual Necessity</td></tr>
          <tr><td>Product improvement and analytics</td><td>Legitimate Interest / Consent</td></tr>
          <tr><td>Sending product updates and newsletters</td><td>Consent (opt-in only)</td></tr>
          <tr><td>Compliance with Indian tax and legal obligations</td><td>Legal Obligation</td></tr>
        </tbody>
      </table>
    </div>

    <div className="legal-section" id="data-sharing" data-section>
      <h2>4. Data Sharing & Disclosure</h2>
      <p>We do not sell your personal data. We share data only in the following circumstances:</p>

      <h3>4.1 Payment Processor — Razorpay</h3>
      <div className="legal-highlight">
        <p>
          To process subscription payments, we share billing-related data with <strong>Razorpay Financial Solutions Pvt. Ltd.</strong>
          Razorpay is an RBI-authorised Payment Aggregator and is fully PCI-DSS compliant. Their privacy
          policy is available at <a href="https://razorpay.com/privacy/" target="_blank" rel="noreferrer">razorpay.com/privacy</a>.
          All payment data is subject to Razorpay's data localisation (stored within India) and security standards.
        </p>
      </div>

      <h3>4.2 Service Providers</h3>
      <p>
        We engage trusted third-party vendors to help operate our platform (e.g., cloud hosting,
        email delivery, analytics). These vendors are bound by Data Processing Agreements (DPAs)
        and may not use your data for their own purposes.
      </p>

      <h3>4.3 Legal Requirements</h3>
      <p>
        We may disclose your information if required by law, court order, or governmental authority,
        including compliance with RBI directives, PMLA requirements, or other Indian regulations.
      </p>

      <h3>4.4 Business Transfers</h3>
      <p>
        In the event of a merger, acquisition, or sale of assets, your data may be transferred to
        the successor entity. We will notify you before your data is transferred and becomes subject
        to a different privacy policy.
      </p>
    </div>

    <div className="legal-section" id="cookies" data-section>
      <h2>5. Cookies & Tracking Technologies</h2>
      <p>
        We use cookies and similar technologies to improve your experience, analyse usage patterns,
        and remember your preferences. Essential cookies are always active; analytics and marketing
        cookies are only placed with your explicit consent.
      </p>
      <p>
        For full details on the cookies we use, categories, and how to manage your preferences, please
        read our <a href="/cookie-policy">Cookie Policy</a>.
      </p>
    </div>

    <div className="legal-section" id="data-retention" data-section>
      <h2>6. Data Retention</h2>
      <table className="legal-table">
        <thead>
          <tr><th>Data Type</th><th>Retention Period</th></tr>
        </thead>
        <tbody>
          <tr><td>Account data (active accounts)</td><td>For the duration of the subscription + 90 days</td></tr>
          <tr><td>Account data (deleted accounts)</td><td>30 days after deletion request, then purged</td></tr>
          <tr><td>Payment & invoice records</td><td>7 years (as required by Indian tax law)</td></tr>
          <tr><td>Support communications</td><td>3 years</td></tr>
          <tr><td>Server/access logs</td><td>90 days</td></tr>
          <tr><td>Cookie consent records</td><td>12 months</td></tr>
        </tbody>
      </table>
    </div>

    <div className="legal-section" id="your-rights" data-section>
      <h2>7. Your Rights Under DPDPA 2023</h2>
      <p>As a data principal under India's DPDPA 2023, you have the following rights:</p>
      <ul>
        <li><strong>Right to Access:</strong> Request a summary of personal data we hold about you.</li>
        <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete data.</li>
        <li><strong>Right to Erasure:</strong> Request deletion of your personal data (subject to legal obligations).</li>
        <li><strong>Right to Grievance Redressal:</strong> File a complaint with our Grievance Officer.</li>
        <li><strong>Right to Nominate:</strong> Nominate another individual to exercise rights on your behalf in case of death or incapacity.</li>
        <li><strong>Right to Withdraw Consent:</strong> Withdraw previously given consent for non-essential data processing.</li>
      </ul>
      <p>
        To exercise any of these rights, email us at <a href="mailto:info@westernflex.com">info@westernflex.com</a>.
        We will respond within <strong>30 days</strong> of receiving your request.
      </p>
      <div className="legal-highlight">
        <p>
          <strong>Grievance Officer:</strong> For privacy-related complaints, contact our Grievance Officer
          at <a href="mailto:info@westernflex.com">info@westernflex.com</a>. Complaints will be
          acknowledged within 48 hours and resolved within 30 days.
        </p>
      </div>
    </div>

    <div className="legal-section" id="security" data-section>
      <h2>8. Data Security</h2>
      <p>We implement industry-standard security measures to protect your data:</p>
      <ul>
        <li>All data in transit is encrypted using TLS 1.2 or higher</li>
        <li>Passwords are hashed using bcrypt with salting</li>
        <li>Access to production databases is restricted by role and IP allowlisting</li>
        <li>Regular security audits and vulnerability assessments</li>
        <li>Payment data is processed through Razorpay's PCI-DSS certified infrastructure</li>
        <li>Multi-factor authentication available for admin accounts</li>
      </ul>
      <p>
        Despite our best efforts, no security system is impenetrable. In the event of a data breach
        that affects your rights, we will notify you and the relevant authorities within the timelines
        mandated by applicable law.
      </p>
    </div>

    <div className="legal-section" id="children" data-section>
      <h2>9. Children's Privacy</h2>
      <p>
        Our Service is designed for business use and is not directed at individuals under the age of 18.
        We do not knowingly collect personal data from minors. If we become aware that a minor has
        provided us with personal data, we will promptly delete it.
      </p>
    </div>

    <div className="legal-section" id="changes" data-section>
      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy periodically to reflect changes in our practices, technology,
        or legal requirements. When we make material changes, we will:
      </p>
      <ul>
        <li>Update the "Last updated" date at the top of this page</li>
        <li>Send an email notification to registered account holders</li>
        <li>Display a notice within the platform for 30 days</li>
      </ul>
      <p>Your continued use of the Service after the effective date constitutes acceptance of the updated policy.</p>
    </div>

    <div className="legal-section" id="contact" data-section>
      <h2>11. Contact Us</h2>
      <p>For any privacy-related questions, requests, or concerns:</p>
      <table className="legal-table">
        <tbody>
          <tr><td><strong>Email</strong></td><td><a href="mailto:info@westernflex.com">info@westernflex.com</a></td></tr>
          <tr><td><strong>Grievance Officer</strong></td><td><a href="mailto:info@westernflex.com">info@westernflex.com</a></td></tr>
          <tr><td><strong>Support</strong></td><td><a href="mailto:info@westernflex.com">info@westernflex.com</a></td></tr>
          <tr><td><strong>Response Time</strong></td><td>Within 30 business days</td></tr>
        </tbody>
      </table>
    </div>
  </LegalPageLayout>
);

export default PrivacyPolicy;
